import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Mail, Clock, Send, CheckCircle2, Sparkles, AlertCircle } from "lucide-react";
import { useState } from "react";

// TODO: Replace with your actual Google Apps Script Web App URL
const GOOGLE_SCRIPT_URL = "https://script.google.com/macros/s/AKfycbyVDw2n_HaHuPLNOebSzcHZTYAEg251bHztRB0T8ZIJS3z8mOJ0ShHQPmQALV8FT3FR8g/exec";

export function ContactUs({
  backgroundColor,
  title = "Have Questions? Let's Talk!",
  subtitle = "We're here to help with anything you need"
}: {
  backgroundColor?: string;
  title?: string;
  subtitle?: string;
}) {
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: "",
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setError(null);

    try {
      // We use 'no-cors' mode because Google Apps Script redirects responses
      // and standard CORS checks often fail with simple fetch on GAS.
      // However, 'no-cors' means we can't read the response body.
      // We assume success if no network error is thrown.
      await fetch(GOOGLE_SCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(formData),
      });

      setIsSubmitting(false);
      setIsSubmitted(true);

      // Reset form after 3 seconds
      setTimeout(() => {
        setFormData({ name: "", email: "", message: "" });
        setIsSubmitted(false);
      }, 3000);
    } catch (err) {
      console.error("Form submission error:", err);
      setError("Something went wrong. Please try again later.");
      setIsSubmitting(false);
    }
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    setFormData(prev => ({
      ...prev,
      [e.target.name]: e.target.value
    }));
  };

  return (
    <section id="contact" className={`py-20 md:py-32 ${backgroundColor}`}>
      <div className="container mx-auto px-4">
        <div className="text-center max-w-3xl mx-auto mb-16">
          <Badge className="mb-4 bg-primary/10 text-primary border-primary/20 hover:bg-primary/20">Get in Touch</Badge>
          <h2 className="text-3xl md:text-5xl font-bold mb-4">
            {title}
          </h2>
          <p className="text-lg text-muted-foreground">
            {subtitle}
          </p>
        </div>

        <div className="max-w-6xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12">
            {/* Left Side - Info */}
            <div className="lg:col-span-2 space-y-8">
              <div>
                <h3 className="text-2xl font-bold mb-4">Drop us a line</h3>
                <p className="text-muted-foreground leading-relaxed mb-6">
                  Whether you have a question about features, pricing, need support, or anything else, our team is ready to answer all your questions.
                </p>
              </div>

              <div className="space-y-6">
                <Card className="p-6 bg-gradient-to-br from-primary/10 to-purple-500/10 border-2 border-primary/30 hover:border-primary transition-all hover:scale-105 duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Mail className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Email Us</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        Send us an email anytime
                      </p>
                      <a
                        href="mailto:support@photoshoprequest.app"
                        className="text-primary hover:underline text-sm font-semibold"
                      >
                        support@photoshoprequest.app
                      </a>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-primary/10 to-purple-500/10 border-2 border-primary/30 hover:border-primary transition-all hover:scale-105 duration-300">
                  <div className="flex items-start gap-4">
                    <div className="w-12 h-12 rounded-xl bg-gradient-to-br from-primary to-purple-500 flex items-center justify-center flex-shrink-0 shadow-lg">
                      <Clock className="h-6 w-6 text-white" />
                    </div>
                    <div>
                      <h4 className="font-semibold mb-1">Response Time</h4>
                      <p className="text-sm text-muted-foreground mb-2">
                        We typically respond within
                      </p>
                      <p className="text-primary text-sm font-semibold">
                        24 hours or less
                      </p>
                    </div>
                  </div>
                </Card>

                <Card className="p-6 bg-gradient-to-br from-green-500/10 to-emerald-500/10 border-2 border-green-500/30">
                  <div className="flex items-start gap-3">
                    <Sparkles className="h-5 w-5 text-green-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-muted-foreground">
                      <span className="font-semibold text-foreground">Quick tip:</span> Check our{" "}
                      <a href="/faq" className="text-primary hover:underline font-semibold">
                        FAQ section
                      </a>{" "}
                      for instant answers to common questions!
                    </p>
                  </div>
                </Card>
              </div>
            </div>

            {/* Right Side - Form */}
            <div className="lg:col-span-3">
              <Card className="p-8 lg:p-10 from-card to-card/50 border-2 border-primary/30 shadow-2xl backdrop-blur-xl bg-muted/20">
                {isSubmitted ? (
                  <div className="text-center py-16">
                    <div className="w-20 h-20 rounded-full bg-gradient-to-br from-green-500/20 to-emerald-500/20 flex items-center justify-center mx-auto mb-6 animate-in zoom-in duration-500">
                      <CheckCircle2 className="h-10 w-10 text-green-500" />
                    </div>
                    <h3 className="text-3xl font-bold mb-3">Message Sent! 🎉</h3>
                    <p className="text-muted-foreground text-lg">
                      Thanks for reaching out. We'll get back to you within 24 hours.
                    </p>
                  </div>
                ) : (
                  <form onSubmit={handleSubmit} className="space-y-6">
                    {error && (
                      <div className="p-4 rounded-lg bg-destructive/10 text-destructive flex items-center gap-2">
                        <AlertCircle className="h-5 w-5" />
                        <p>{error}</p>
                      </div>
                    )}

                    <div>
                      <Label htmlFor="name" className="text-base">Your Name *</Label>
                      <Input
                        id="name"
                        name="name"
                        type="text"
                        placeholder="John Doe"
                        value={formData.name}
                        onChange={handleChange}
                        required
                        className="h-12 mt-2 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="email" className="text-base">Email Address *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        placeholder="john@example.com"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="h-12 mt-2 text-base"
                      />
                    </div>

                    <div>
                      <Label htmlFor="message" className="text-base">Your Message *</Label>
                      <Textarea
                        id="message"
                        name="message"
                        placeholder="Tell us what you need help with..."
                        value={formData.message}
                        onChange={handleChange}
                        required
                        rows={7}
                        className="resize-none mt-2 text-base"
                      />
                    </div>

                    <Button
                      type="submit"
                      size="lg"
                      className="w-full h-14 text-lg bg-gradient-to-r from-primary to-purple-600 hover:from-primary/90 hover:to-purple-600/90 shadow-lg hover:shadow-xl transition-all duration-300"
                      disabled={isSubmitting}
                    >
                      {isSubmitting ? (
                        <span className="flex items-center gap-2">
                          <div className="w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                          Sending...
                        </span>
                      ) : (
                        <>
                          <Send className="mr-2 h-5 w-5" />
                          Send Message
                        </>
                      )}
                    </Button>

                    <p className="text-xs text-center text-muted-foreground">
                      We'll never share your information with anyone else.
                    </p>
                  </form>
                )}
              </Card>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

